import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider } from '../contexts/AuthContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { BreadcrumbProvider } from '../store/BreadcrumbContext';
import { PermissionProvider } from '../store/PermissionContext';
import { queryClient } from '../lib/queryClient';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { ImagePreviewProvider } from '../components/ui/ImagePreview/ImagePreviewProvider';
import { GOOGLE_CLIENT_ID } from '../constants/env';

/**
 * Composes every provider the app needs in a single place. Mirrors
 * RW-react-core-standards `frontend-architecture.md` §9 — the App root
 * is kept thin (`<AppProviders><AppRoutes /></AppProviders>`) and provider
 * order is owned here.
 *
 * Order (outer → inner):
 *   1. GoogleOAuthProvider — leaves OAuth iframe alone if rendered above
 *      the router so route changes don't remount it.
 *   2. ThemeProvider       — surfaces `data-theme` on <html> before any
 *      consumer paints, avoiding a flash of unthemed content.
 *   3. BrowserRouter       — every consumer below can call `useNavigate`
 *      and `useLocation`.
 *   4. AuthProvider        — depends on router (uses `useNavigate` for
 *      post-login redirects).
 *   5. PermissionProvider  — depends on auth (reads the current user).
 *   6. BreadcrumbProvider  — transient UI state.
 *   7. ImagePreviewProvider — innermost; hosts the ONE full-screen image
 *      viewer every product thumbnail opens (renders nothing while closed).
 */
/*
 * Dev-only verification scaffold. Loaded once via dynamic import so the
 * helper module is tree-shaken from production bundles. Open Chrome DevTools
 * → Console and run `await window.__rwCache.runSmokeProbe()` to write +
 * read a sample record per mastery entity; results appear under
 * Application → IndexedDB → MasteryData → VersionUpdateStore.
 *
 * REMOVE BEFORE MERGING the foundation PR — this is verification only.
 */
if (process.env.NODE_ENV !== 'production') {
  void import('../lib/devCacheDebug').then((m) => m.installCacheDebug());
}

interface AppProvidersProps {
  children: React.ReactNode;
}

const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <AuthProvider>
            <PermissionProvider>
              <BreadcrumbProvider>
                <QueryClientProvider client={queryClient}>
                  <ErrorBoundary>
                    <ImagePreviewProvider>{children}</ImagePreviewProvider>
                  </ErrorBoundary>
                </QueryClientProvider>
              </BreadcrumbProvider>
            </PermissionProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default AppProviders;
